import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";
import { Badge, Button, Card, Col, Row, Stack } from "react-bootstrap";
import { deleteProgram } from "../../../services/httpClient";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import { ChevronButton, levelSummaryParts, plural } from "./ProgramLevelCard";

// a program is a pathway once any of its levels has attached items or advancement criteria
export const isPathwayProgram = (prog: any) =>
  (prog?.programLevels || []).some(
    (l: any) =>
      l.goalTemplates?.length > 0 ||
      l.assessmentTypes?.length > 0 ||
      l.noteTypes?.length > 0 ||
      (l.min_days_before_advance !== null && l.min_days_before_advance !== undefined) ||
      (l.advancement_notes !== null && l.advancement_notes !== undefined && l.advancement_notes !== ""),
  );

interface CardState {
  program: any;
  getItems: any;
  onEdit: (programId: any) => void;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

// One program in the list: name, summary and Edit / Delete. Levels show read-only when expanded;
// everything is edited in ProgramEditor.
const ProgramCard = ({ program, getItems, onEdit, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [expanded, setExpanded] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const levels: any[] = program?.programLevels || [];
  const itemCount = levels.reduce(
    (n: number, l: any) => n + (l.goalTemplates?.length || 0) + (l.assessmentTypes?.length || 0) + (l.noteTypes?.length || 0),
    0,
  );

  const onClickDeleteFinal = () => {
    deleteProgram(program?.id)
      .then(() => {
        setShowToast(true);
        setToastText("Program Deleted");
        setToastBody(program?.name);
        setIsDeleting(false);
        getItems(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({ top: 0, behavior: "auto" });
      });
  };

  if (isDeleting) {
    return (
      <Card>
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Delete {program?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={onClickDeleteFinal}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button
              className="app-primary-color button"
              onClick={() => {
                setIsDeleting(false);
                setErrMessage(undefined);
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div>
      <Row className="align-items-center">
        <Col xs={9}>
          <Stack direction="horizontal" gap={3}>
            <ChevronButton open={expanded} onClick={() => setExpanded(!expanded)} tooltip={expanded ? "Collapse" : "Expand"} />
            <div style={{ minWidth: 0 }}>
              <h6 className="mb-0 text-truncate">{program.name}</h6>
              <div className="text-muted small">
                {plural(levels.length, "level")} · {plural(itemCount, "item")}
              </div>
            </div>
          </Stack>
        </Col>
        <Col xs={3} className="d-flex justify-content-end">
          <Stack direction="horizontal" gap={3} className="justify-content-end">
            {isPathwayProgram(program) && (
              <Badge bg="none" className="app-secondary-color">
                Pathway
              </Badge>
            )}
            <IconEmbeddedButton onClickButton={() => onEdit(program.id)} tooltipText={"Edit Program"} icon={faEdit} />
            <div className="vr" />
            <IconEmbeddedButton onClickButton={() => setIsDeleting(true)} tooltipText={"Delete Program"} icon={faTrash} />
          </Stack>
        </Col>
      </Row>
      {expanded && (
        <div className="mt-3 pt-2 border-top small">
          {levels.length === 0 && <div className="text-muted">No levels yet.</div>}
          {levels.map((l: any) => {
            const parts = levelSummaryParts(l);
            return (
              <div key={l.id} className="py-1">
                <span>{l.name}</span>
                <span className="text-muted">
                  {" · "}
                  {parts.length > 0 ? parts.join(" · ") : "Nothing attached"}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProgramCard;
